import React, { useRef, useState, useEffect } from 'react';
import ActionButtons from './editor/ActionButtons';
import BeforeAfterSlider from './editor/BeforeAfterSlider';
import FullscreenOverlay from './editor/FullscreenOverlay';
import MaskOverlay from './editor/MaskOverlay';
import FilePicker from './editor/FilePicker';
import History from './editor/History';

import { useAppStore } from '../store/useAppStore';
import { useTranslations } from '../hooks/useTranslations';
import { downloadFile, processMessage } from '../api';

const Canvas = () => {
  const { 
    sidebarOpen, 
    currentImage, 
    imageHistory, 
    originalImage,
    addChatMessage,
    addToImageHistory,
    sliderVisible,
    setSliderVisible,
    setLoading,
    loading,
    currentMaskUrl,
    setCurrentMask,
    maskPreviewVisible,
    maskPreviewOpacity,

    // Session
    currentSessionId,
    sessionHistory,
    createSession,
    removeLastFromSession,
    appendCurrentData
  } = useAppStore();

  const { t } = useTranslations();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [maskVisible, setMaskVisible] = useState(false);
  const [segmentationVisible, setSegmentationVisible] = useState(false);
  const mainImageRef = useRef(null);
  const imageContainerRef = useRef(null);


  // Professional preset variants for the left vertical column
  const presetPreviews = [
    // { key: 'enhance', label: t('enhance'), filter: 'contrast(1.08) saturate(1.15) brightness(1.05) blur(0px)', icon: '✨' },
    { key: 'bw', label: t('bw'), filter: 'grayscale(1) contrast(1.15) brightness(0.98)', icon: '🎭' },
    { key: 'warm', label: t('warm'), filter: 'saturate(1.25) hue-rotate(-15deg) brightness(1.05) sepia(0.1)', icon: '🔥' },
    { key: 'cool', label: t('cool'), filter: 'saturate(1.15) hue-rotate(15deg) brightness(1.02) contrast(1.05)', icon: '❄️' },
    { key: 'vivid', label: t('vivid'), filter: 'saturate(1.4) contrast(1.15) brightness(1.02)', icon: '🌈' },
    { key: 'cinematic', label: t('cinema'), filter: 'contrast(1.12) saturate(1.1) hue-rotate(-5deg) brightness(0.98)', icon: '🎬' },
    { key: 'sepia', label: t('vintage'), filter: 'sepia(1) saturate(0.8) contrast(1.05)', icon: '📸' }
  ];

  const goBack = async () => {
    await removeLastFromSession()
  }


  const applyPresetPrompt = async (key) => {
    if (!key || !currentSessionId) return;
    try {
      await setLoading(true)
      console.log('💬 Sending chat message to backend...');
      addChatMessage({ type: 'user', content: `preset: ${key}`, timestamp: Date.now() });
      const result = await processMessage(currentSessionId, sessionHistory.at(-1).currentDataId, sessionHistory.at(-1).currentAuxDataId, `preset:${key}`) 
      
      console.log('taskResult:', result);
      await appendCurrentData(result.task.output_data_id, null)

      const image = await downloadFile(result.task.output_data_id)
      await addToImageHistory(image, `preset: ${key}`)

      // Send preset command to backend (bypasses heavy model)
      addChatMessage({ type: 'ai', content: result.message.response, timestamp: Date.now() });
      await setLoading(false)
    } catch (e) {
      console.error('Preset apply failed', e);
      addChatMessage({ type: 'ai', content: 'There was a problem applying that preset. Please try again.', timestamp: Date.now() })
    }
  };

  return (
        <div className={`image-container ${!sidebarOpen ? 'sidebar-collapsed' : ''} ${false ? 'edit-mode' : ''}`}>
          <div className="image-wrapper">
            {/* Welcome/Upload Area or Selected Image */}
            {!currentImage ? (
              <FilePicker />
            ) : (
              <div className="selected-image-container">
                {/* Left vertical preset column */}
                {currentImage && (
                  <div className="left-preset-column">
                    {presetPreviews.map((p) => (
                      <button
                        key={p.key}
                        className="preset-thumb"
                        title={`Apply ${p.label} preset`}
                        onClick={() => applyPresetPrompt(p.key)}
                      >
                        <img src={currentImage} alt={p.label} style={{ filter: p.filter || 'none' }} />
                        <div className="preset-label">
                          <span className="preset-icon">{p.icon}</span>
                          {p.label}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                <ActionButtons
                  onNew={async () => {
                    await createSession();
                    console.log('New session started - cleared all state');
                  }}
                  onBack={async () => {
                    await goBack();
                    console.log('Going back one step');
                  }}
                  onRestart={async () => {
                    await createSession();
                  }}
                  onLike={() => {}}
                  onSave={() => {
                    if (currentImage) {
                      const link = document.createElement('a');
                      link.href = currentImage;
                      link.download = `esper-ai-${Date.now()}.png`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  onShare={() => {}}
                  onSlider={() => setSliderVisible(!sliderVisible)}
                  sliderActive={sliderVisible && imageHistory.length >= 2}
                  showSlider={imageHistory.length >= 2}
                  canGoBack={sessionHistory.length > 1}
                  canRestart={sessionHistory.length > 1}
                  onBrush={() => setMaskVisible(v => !v)}
                  brushActive={maskVisible}
                  onSegmentation={async () => {
                    if (!currentImage) {
                      console.log('❌ No image available for segmentation')
                      return
                    }

                    console.log('🎯 Starting segmentation...')
                    
                    try {
                      // Convert image to base64 if it's a URL
                      let imageData = currentImage
                      if (currentImage.startsWith('http') || currentImage.startsWith('/')) {
                        console.log('🔄 Converting image URL to base64...')
                        const response = await fetch(currentImage)
                        const blob = await response.blob()
                        imageData = await new Promise((resolve) => {
                          const reader = new FileReader()
                          reader.onloadend = () => resolve(reader.result)
                          reader.readAsDataURL(blob)
                        })
                      }

                      // Run segmentation
                      console.log('📤 Sending segmentation request...')
                      const response = await fetch('/api/segment', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          image: imageData
                        })
                      })

                      if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                      }

                      const data = await response.json()
                      console.log(`✅ Segmentation complete: ${data.count} masks generated`)

                      // Store masks for display
                      if (data.masks && data.masks.length > 0) {
                        console.log(`🎯 Loading ${data.masks.length} masks for display...`)
                        setSegmentationMasks(data.masks)
                        setSegmentationVisible(true)
                        setSelectedMaskId(null)
                      }

                    } catch (error) {
                      console.error('❌ Segmentation error:', error)
                    }
                  }}
                  segmentationActive={segmentationVisible}

                />
                
                {sliderVisible && originalImage && currentImage && originalImage !== currentImage ? (
                  <div className="main-image-container" id="mainImageContainer" ref={imageContainerRef}>
                    <img 
                      ref={mainImageRef}
                      src={currentImage} 
                      alt="Selected image" 
                      className="main-image"
                      style={{
                        opacity: 0, // Hide the underlying image when slider is active
                        transition: 'opacity 0.3s ease'
                      }}
                    />
                    {/* Slider overlay - rendered on top of main image */}
                    <BeforeAfterSlider 
                      beforeSrc={originalImage}
                      afterSrc={currentImage}
                      containerRef={imageContainerRef}
                    />
                  </div>
                ) : (
                  <div className="main-image-container" id="mainImageContainer" ref={imageContainerRef}>
                    <img 
                      ref={mainImageRef}
                      src={currentImage} 
                      alt="Selected image" 
                      className="main-image"
                      style={{
                        opacity: 1, // Image at full opacity - no reduction
                        transition: 'opacity 0.3s ease'
                      }}
                    />

                    {/* Optional mask preview overlay */}
                    {maskPreviewVisible && currentMaskUrl && (
                      <img
                        src={currentMaskUrl}
                        alt="Mask preview"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: maskPreviewOpacity, pointerEvents: 'none', mixBlendMode: 'screen' }}
                      />
                    )}
                    
                    {/* Mask overlay */}
                    {maskVisible && (
                      <MaskOverlay
                        visible={maskVisible}
                        containerRef={imageContainerRef}
                        onApply={async (maskBlob) => {
                          // Save mask URL in UI state (not as gallery entry)
                          const url = URL.createObjectURL(maskBlob)
                          setCurrentMask(url)
                          addChatMessage({ type: 'ai', content: 'Mask saved (not applied). It will be used by future tools.', timestamp: Date.now() })
                          setMaskVisible(false)
                        }}
                        onClose={() => setMaskVisible(false)}
                      />
                    )}
                    
                    <button 
                      className="fullscreen-btn"
                      onClick={() => {
                        setIsFullscreen((v) => !v);
                      }}
                      title="Full Screen"
                    >
                      <i className={isFullscreen ? 'fas fa-compress' : 'fas fa-expand'}></i>
                    </button>
                  </div>
                )}

                {/* Processing overlay shown while backend is working */}
                {loading && (
                  <div className="processing-overlay" role="status" aria-live="polite">
                    <div className="spinner" />
                    <span>Processing…</span>
                  </div>
                )}
              </div>
            )}

            {/* Fullscreen overlay */}
            <FullscreenOverlay
              imageSrc={currentImage}
              isOpen={isFullscreen}
              onClose={() => setIsFullscreen(false)}
            />

            {imageHistory.length > 0 && (
              <History/>
            )}

          </div>
        </div>
  );
};

export default Canvas; 